import React from 'react';
import Icon from '../../../components/AppIcon';
import LicenseUsageMeter from '../../../components/license/LicenseUsageMeter';

const formatBrazilianDate = (isoDate) => {
  if (!isoDate) return '—';

  return new Date(isoDate).toLocaleDateString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
  });
};

const isExpiringSoonDate = (isoDate, daysThreshold = 5) => {
  if (!isoDate) return false;

  const expiryDate = new Date(isoDate);
  if (Number.isNaN(expiryDate.getTime())) return false;

  const now = new Date();
  const diffMs = expiryDate.getTime() - now.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  return diffDays >= 0 && diffDays <= daysThreshold;
};

const LicenseSummaryCard = ({ license, neoCoinsLabel }) => {
  const {
    license_name,
    license_code,
    license_type,
    documents_generated,
    documents_signed,
    max_documents,
    max_signatures,
    expires_at,
    creation_date,
    price,
    is_shared,
    max_members,
    is_active,
  } = license;

  const isExpired = is_active === 'false';
  const isExpiringSoon = !isExpired && isExpiringSoonDate(expires_at, 5);

  return (
    <div
      className={[
        'rounded-xl p-6 shadow-elevation-sm border',
        isExpired ? 'bg-error/10 border-error/30' : isExpiringSoon ? 'bg-warning/10 border-warning/30' : 'bg-card border-border',
      ].join(' ')}
    >
      {isExpired && (
        <div className="mb-4 flex items-center gap-2 px-4 py-2 bg-error/10 border border-error/20 rounded-lg">
          <Icon name="AlertCircle" size={16} className="text-error flex-shrink-0" />
          <p className="text-xs md:text-sm text-error font-medium">Licença expirada</p>
        </div>
      )}

      {!isExpired && isExpiringSoon && (
        <div className="mb-4 flex items-center gap-2 px-4 py-2 bg-warning/10 border border-warning/20 rounded-lg">
          <Icon name="Clock" size={16} className="text-warning flex-shrink-0" />
          <p className="text-xs md:text-sm text-warning font-medium">Expirando em breve</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <InfoItem
          icon="BadgeCheck"
          label="Nome da licença"
          value={license_name}
        />

        <InfoItem
          icon="Hash"
          label="Código da licença"
          value={license_code}
        />

        <InfoItem
          icon="Tag"
          label="Tipo da licença"
          value={license_type}
        />

        <InfoItem
          icon="FileText"
          label="Consumo"
          value={`${documents_generated ?? 0} documentos gerados`}
        />

        <InfoItem
          icon="PenTool"
          label="Assinatura de Documentos"
          value={documents_signed ?? 0}
        />

        <InfoItem
          icon="Files"
          label="Limite de documentos"
          value={max_documents}
        />

        <InfoItem
          icon="Edit3"
          label="Limite de assinaturas"
          value={max_signatures}
        />

        <InfoItem
          icon="Calendar"
          label="Data de expiração"
          value={formatBrazilianDate(expires_at)}
        />

        <InfoItem
          icon="Clock"
          label="Data de aquisição"
          value={formatBrazilianDate(creation_date)}
        />

        <InfoItem
          icon="DollarSign"
          label="Preço"
          value={price === 0 ? 'Gratuito' : `R$ ${price}`}
        />

        {is_shared && (
          <InfoItem
            icon="Users"
            label="Limite de compartilhamento"
            value={`${max_members} usuários`}
          />
        )}

        {neoCoinsLabel != null && (
          <InfoItem
            icon="Coins"
            label="Neo Coins"
            value={neoCoinsLabel}
            iconClassName="text-amber-500"
          />
        )}
      </div>

      {!isExpired && (max_documents != null || max_signatures != null) ? (
        <div className="mt-6 pt-6 border-t border-border grid grid-cols-1 md:grid-cols-2 gap-4">
          <LicenseUsageMeter
            label="Uso de geração de documentos"
            usedRaw={documents_generated}
            maxRaw={max_documents}
          />
          <LicenseUsageMeter
            label="Uso de assinaturas"
            usedRaw={documents_signed}
            maxRaw={max_signatures}
          />
        </div>
      ) : null}
    </div>
  );
};

const InfoItem = ({ icon, label, value, iconClassName }) => (
  <div className="flex gap-3">
    <div className="mt-0.5">
      <Icon
        name={icon}
        size={18}
        className={iconClassName ?? 'text-muted-foreground'}
      />
    </div>

    <div>
      <p className="text-xs font-medium text-muted-foreground mb-1">
        {label}
      </p>

      <p className="text-sm font-semibold text-foreground">
        {value ?? '—'}
      </p>
    </div>
  </div>
);

export default LicenseSummaryCard;